import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const MessageBubble = ({ message, isUser = false, agentInitials = 'SM' }) => {
  const [selectedPersons, setSelectedPersons] = useState(new Set());
  const [selectionCompleted, setSelectionCompleted] = useState(false);

  const handleCTAClick = async (action) => {
    try {
      if (action?.type === 'CALL') {
        window.location.href = `tel:${action?.phone}`;
      } else if (action?.type === 'REDIRECT') {
        window.open(action?.url, '_blank', 'noopener,noreferrer');
      } else if (action?.type === 'RETRY_SEARCH') {
        // Trigger a search retry
        window.location?.reload();
      } else if (action?.type === 'CTA') {
        // Track CTA click and handle different CTA types
        console.log('CTA clicked:', action?.cta_type);
        
        switch (action?.cta_type) {
          case 'start_bail_process': alert('Starting bail process - form would open here');
            break;
          case 'continue_search': alert('Continue search - search input would be focused');
            break;
          case 'request_callback': alert('Callback request form would open here');
            break;
          case 'learn_options': alert('Educational content would be displayed here');
            break;
          case 'payment_options': alert('Payment options would be displayed here');
            break;
          default:
            console.log('Unknown CTA type:', action?.cta_type);
        }
      }
    } catch (error) {
      console.log('Error handling CTA click:', error);
    }
  };

  const handlePersonSelection = (personId, checked) => {
    const newSelected = new Set(selectedPersons);
    if (checked) {
      newSelected?.add(personId);
    } else {
      newSelected?.delete(personId);
    }
    setSelectedPersons(newSelected);
  };

  const handleSelectionComplete = () => {
    setSelectionCompleted(true);
    
    if (selectedPersons?.size > 0) {
      // User selected someone - trigger bail process
      alert(`Selected ${selectedPersons?.size} person(s) for bail assistance. This would normally open the bail process form.`);
    } else {
      // No selection - offer to search again
      alert('No person selected. This would normally allow the user to search again with different information.');
    }
  };

  // Format message content with markdown-style formatting
  const formatMessage = (text) => {
    if (!text) return '';
    
    return text
      ?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      ?.replace(/\*(.*?)\*/g, '<em>$1</em>')
      ?.replace(/•/g, '•')
      ?.replace(/\n/g, '<br />');
  };

  // Format table data for display
  const formatTableCell = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number' && !isNaN(value)) {
      return value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
    if (Array.isArray(value)) {
      return value?.join(', ');
    }
    return String(value);
  };

  // Render search results table
  const renderSearchResultsTable = (action) => {
    const rows = action?.rows || [];
    
    if (rows?.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center">
            <Icon name="Users" size={16} className="mr-2" />
            Search Results ({rows?.length} found)
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-12 px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Select
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  DOB
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Facility
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Bail Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Charges
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {rows?.map((row, index) => (
                <tr key={row?.person_id || index} className="hover:bg-slate-50">
                  <td className="px-3 py-4 whitespace-nowrap">
                    <Checkbox
                      checked={selectedPersons?.has(row?.person_id)}
                      onChange={(checked) => handlePersonSelection(row?.person_id, checked)}
                      disabled={selectionCompleted}
                      className="text-blue-600"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-slate-900">
                        {row?.first_name} {row?.middle_name ? row?.middle_name + ' ' : ''}{row?.last_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                    {row?.dob ? new Date(row?.dob)?.toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      row?.current_status === 'IN_CUSTODY' ?'bg-red-100 text-red-800'
                        : row?.current_status === 'RELEASED' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                    }`}>
                      {row?.current_status || 'UNKNOWN'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                    {row?.facility_display || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                    {row?.bail_amount ? formatTableCell(row?.bail_amount) : 'No Bail Set'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    <div className="max-w-xs">
                      {row?.charges?.length > 0 
                        ? row?.charges?.join(', ')
                        : 'No charges listed'
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!selectionCompleted && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
            <Button
              onClick={handleSelectionComplete}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Complete Selection
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}>
      <div className="flex items-start space-x-4 max-w-5xl">
        {!isUser && (
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-md">
            <span className="text-white text-sm font-semibold">{agentInitials}</span>
          </div>
        )}
        
        <div className={`relative rounded-2xl px-6 py-4 max-w-4xl transition-all duration-300 hover:shadow-lg ${
          isUser 
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-xl ml-auto transform hover:scale-[1.02]' 
            : 'bg-white/90 backdrop-blur-sm text-slate-800 border border-slate-200 shadow-xl hover:shadow-2xl'
        }`}>
          
          {/* Message timestamp - only for agent messages */}
          {!isUser && (
            <div className="absolute -top-6 left-6 text-xs text-slate-500">
              {new Date(message?.timestamp)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          
          <div 
            className={`text-base leading-relaxed ${isUser ? 'text-white' : 'text-slate-800'}`}
            dangerouslySetInnerHTML={{ __html: formatMessage(message?.content) }}
          />
          
          {/* Search Results Table */}
          {message?.actions?.some(action => action?.type === 'SEARCH_RESULTS_TABLE') && !isUser && (
            <div className="mt-4">
              {message?.actions
                ?.filter(action => action?.type === 'SEARCH_RESULTS_TABLE')
                ?.map((action, index) => (
                  <div key={index}>
                    {renderSearchResultsTable(action)}
                  </div>
                ))
              }
            </div>
          )}
          
          {/* Selection Prompt */}
          {message?.actions?.some(action => action?.type === 'SELECTION_PROMPT') && !isUser && (
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
              <div className="text-sm text-blue-800 font-semibold mb-3 flex items-center">
                <Icon name="CheckCircle" size={16} className="mr-2" />
                {message?.actions?.find(a => a?.type === 'SELECTION_PROMPT')?.message}
              </div>
              {message?.actions?.find(a => a?.type === 'SELECTION_PROMPT')?.remaining_searches > 0 && (
                <div className="text-xs text-blue-600 mt-2">
                  Searches remaining: {message?.actions?.find(a => a?.type === 'SELECTION_PROMPT')?.remaining_searches}
                </div>
              )}
            </div>
          )}
          
          {/* Enhanced CTAs and Actions */}
          {message?.actions && message?.actions?.length > 0 && !isUser && (
            <div className="mt-4 flex flex-wrap gap-3">
              {message?.actions
                ?.filter(action => ['CALL', 'CTA', 'REDIRECT', 'RETRY_SEARCH']?.includes(action?.type))
                ?.map((action, index) => {
                if (action?.type === 'CALL') {
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => handleCTAClick(action)}
                      className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-all duration-200 font-medium"
                    >
                      <Icon name="Phone" size={14} className="mr-2" />
                      {action?.label || 'Call Now'}
                    </Button>
                  );
                }
                
                if (action?.type === 'REDIRECT') {
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => handleCTAClick(action)}
                      className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 transition-all duration-200 font-medium"
                    >
                      <Icon name="ExternalLink" size={14} className="mr-2" />
                      {action?.label || 'Visit Website'}
                    </Button>
                  );
                }
                
                if (action?.type === 'RETRY_SEARCH') {
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => handleCTAClick(action)}
                      className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 font-medium"
                    >
                      <Icon name="RefreshCw" size={14} className="mr-2" />
                      {action?.label || 'Try Again'}
                    </Button>
                  );
                }
                
                if (action?.type === 'CTA') {
                  return (
                    <Button
                      key={index}
                      variant={action?.cta_type === 'start_bail_process' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleCTAClick(action)}
                      className={action?.cta_type === 'start_bail_process' ?'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 font-medium' :'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 font-medium'
                      }
                    >
                      {action?.label || 'Learn More'}
                    </Button>
                  );
                }
                
                return null;
              })}
            </div>
          )}
          
          {/* Enhanced Bail Prompt */}
          {message?.actions?.some(action => action?.type === 'BAIL_PROMPT') && !isUser && (
            <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl">
              <div className="text-sm text-emerald-800 font-semibold mb-3 flex items-center">
                <Icon name="CheckCircle" size={16} className="mr-2" />
                {message?.actions?.find(a => a?.type === 'BAIL_PROMPT')?.message}
              </div>
              <div className="flex gap-3">
                <Button variant="default" size="sm" className="bg-emerald-600 hover:bg-emerald-700 font-medium">
                  ✅ Yes, Start Process
                </Button>
                <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium">
                  📚 Learn Options First
                </Button>
              </div>
            </div>
          )}
          
          {/* Enhanced Educational Content */}
          {message?.actions?.some(action => action?.type === 'EDUCATIONAL') && !isUser && (
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
              <div className="text-sm text-blue-800 flex items-start">
                <Icon name="BookOpen" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span>{message?.actions?.find(a => a?.type === 'EDUCATIONAL')?.message}</span>
              </div>
            </div>
          )}
        </div>
        
        {isUser && (
          <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-md">
            <Icon name="User" size={16} color="white" />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;